import { Injector, InjectionToken } from "@angular/core";
import { Observable, Subscription, BehaviorSubject ,  of } from "rxjs";
import { CasingPlanAlertService, GISServiceConfig } from "../services/casing-plan-alert.service";
import { NewCasingPlanComponent } from "../new-casing-plan.component";
import { GISConfig } from "../../../../gis/models/gis-config.model";
import { concatMap, tap, mergeMap } from "rxjs/operators";
import { CasingPlanModel, CasingType, SegmentWizardSteps, WellSegmentGisDataModel, WellDirection, UNMAPPED_USDW } from "../models/casing-plan.model";
import { isDefined } from "../models/casing-plan-functions.model";
import { WellType } from "../../../../common/models/well.model";



export abstract class Alert {
    protected casingPlan: CasingPlanModel;
    protected casingPlanComponent: NewCasingPlanComponent;

    alertType: AlertType = AlertType.Error;
    applicable: boolean;
    appliesToCasing: CasingType[] = [];
    showOnSteps: SegmentWizardSteps[] = [];
    resolved: boolean;
    name: string;
    text: string;
    thickness: number = undefined;

    abstract validate(): Observable<boolean> | boolean;

    constructor(protected injector: Injector) {
        this.casingPlanComponent = injector.get(CASING_PLAN_TOKEN);
        this.casingPlan = this.casingPlanComponent.casingPlan;
        this.applicable = false;
        this.resolved = false;
    }
}
export type Coordinate = { latitude: number, longitude: number };

export abstract class GISAlert extends Alert {
    protected featureData: WeakMap<{ config: GISServiceConfig, coordinate: Coordinate }, any> = new WeakMap();
    protected coordinates: { config: GISServiceConfig, coordinate: Coordinate }[] = [];
    protected serviceConfig: GISServiceConfig;
    protected GISAlertService: CasingPlanAlertService;
    protected GISConfig: GISConfig;
    public response: any;
    public children: Alert[] = [];
    public isApplicable$ = new BehaviorSubject<boolean>(false); //so that one alert can monitor the applicability of another

    constructor(injector: Injector) {
        super(injector);
        this.GISAlertService = injector.get(CasingPlanAlertService);
        this.setUp(window["GIS.Conf"] as GISConfig);
    }

    abstract setUp(config: GISConfig);

    clearCache(): void {
        this.coordinates = [];
        this.featureData = new WeakMap();
    }

    getDataSource(coordinate: Coordinate, config: GISServiceConfig = this.serviceConfig, serviceType: ServiceType = ServiceType.Feature, options?: { distanceBuffer: number, units: string, query?: string }): Observable<any> {
        let savedKey = this.coordinates.find(x =>
            x.coordinate.latitude === coordinate.latitude
            && x.coordinate.longitude === coordinate.longitude
            && x.config === config);

        let data$: Observable<any> = of(null);
        if (serviceType === ServiceType.Feature) {
            data$ = this.GISAlertService.queryFeatureLayer(config, {
                latitude: coordinate.latitude,
                longitude: coordinate.longitude,
            }, options);
        }
        else {
            data$ = this.GISAlertService.queryImageServer(config, {
                latitude: coordinate.latitude,
                longitude: coordinate.longitude,
            });
        }

        let key = { config: config, coordinate: coordinate };
        if (!savedKey)
            this.coordinates.push(key);
        else
            data$ = of(this.featureData.get(savedKey));

        return data$.pipe(tap((result) => {
            this.featureData.set(savedKey || key, result);
        }));
    }
}

export enum ServiceType {
    Feature,
    Raster,
    Image
}

export enum AlertType {
    Information,
    Error
}

export enum YearlyFeatureLayers {
    Karst = "33",
    H2S = "16",
    Trempealeau = "20",
    Townships = "21",
    ThinTill = "22",
    SolutionsMining = "25",
    ShallowSurfaceCasing = "26",
    StorageFields = "23",
    MeccaPoolLakePlain = "28",
    NHDFlowline = "8",
    NHDWaterBody = "29",
    NHDArea = "30"
}

export enum BiWeeklyFeatureLayers {
    UndergroundMines = "4",
    AbandonedUndergroundMines = "14",
}

export enum DailyFeatureLayers {
    InjectionWellAORQtr = "7",
    WaterWells = "2"
}

export const CASING_PLAN_TOKEN = new InjectionToken<NewCasingPlanComponent>("Casing Plan");

export class NonMappableUSDWAlert extends GISAlert {
    alertType = AlertType.Error;
    appliesToCasing = [CasingType.Conductor];
    showOnSteps = [SegmentWizardSteps.Casing, SegmentWizardSteps.Points, SegmentWizardSteps.ReviewWellSegment];
    name = "Non-mappable USDW, no water well or spring";
    text = "Your proposed surface location is within a non-mappable USDW area." +
        " Shallow aquifers must be protected. A minimum of 100' of Conductor casing is required. (OAC 1501:9-1-08(M)(4)(f)(iii))";
    children = [];



    setUp(config: GISConfig) {
        this.serviceConfig = {
            url: config.usdwAreasUrl,
            layerId: "0"
        }
    }

    validate(): Observable<boolean> {
        const point = { latitude: this.casingPlanComponent.latitude, longitude: this.casingPlanComponent.longitude };
        let coordinate: Coordinate = { latitude: point.latitude, longitude: point.longitude };
        let data$ = this.getDataSource(coordinate);

        return data$.pipe(concatMap((result) => {
            if (result && result.features && result.features.length > 0
                && result.features[0].attributes.USDWAREAS === UNMAPPED_USDW) {
                this.response = result;

                const wellAlert = <GISAlert>this.casingPlanComponent.constructionService.observableAlerts.find(g => g instanceof WaterWellsNonMappableUSDWAlert);
                wellAlert.isApplicable$.subscribe((applies) => {
                    this.applicable = !applies;
                    if (this.applicable) {
                        let conductor = this.casingPlan.wellSegments.find(f => f.casingType.definition === CasingType.Conductor);
                        this.resolved = conductor && conductor.casingLength >= 100
                    } else {
                        this.resolved = true;
                    }
                });
            } else {
                this.applicable = false;
                this.resolved = true;
            }
            return of(this.resolved);
        }));
    }
}

export class ShallowSurfaceCasingAlert extends GISAlert {
    alertType = AlertType.Error;
    appliesToCasing = [CasingType.Conductor, CasingType.Surface, CasingType.Production];
    showOnSteps = [SegmentWizardSteps.Casing, SegmentWizardSteps.ReviewWellSegment];
    name = "Shallow Surface Casing";
    text = "Your proposed surface location is within an area where shallow methane has been encountered." +
        " Conductor casing is required. (ORC 1509.06(F)), (OAC 1501:9-1-08(M)(3)(a)(iv))";

    setUp(config: GISConfig) {
        this.serviceConfig = {
            url: config.yearlyGISDataUrl,
            layerId: YearlyFeatureLayers.ShallowSurfaceCasing
        };
    }

    validate(): Observable<boolean> {
        const point = { latitude: this.casingPlanComponent.latitude, longitude: this.casingPlanComponent.longitude };
        let coordinate: Coordinate = { latitude: point.latitude, longitude: point.longitude };
        let data$ = this.getDataSource(coordinate);

        return data$.pipe(concatMap((result) => {
            if (result && result.features && result.features.length > 0) {
                this.response = result;
                this.applicable = true;
                this.resolved = this.casingPlan.wellSegments.findIndex(f => f.casingType.definition === CasingType.Conductor) !== -1;
            } else {
                this.applicable = false;
                this.resolved = true;
            }
            return of(this.resolved);
        }));
    }
}

export class MunicipalProtectionAreaAlert extends GISAlert {
    alertType = AlertType.Error;
    appliesToCasing = [CasingType.Conductor, CasingType.Surface];
    showOnSteps = [SegmentWizardSteps.Casing, SegmentWizardSteps.ReviewWellSegment];
    name = "Municipal Wellhead Protection Area";
    text = "Your proposed location is within a source water protection area. Shallow aquifers must be protected (ORC 1509.06(F))";

    EMSConfig: GISServiceConfig;

    setUp(config: GISConfig) {
        this.serviceConfig = {
            url: config.fiveYearTravelDataUrl,
            layerId: '0'
        };

        this.EMSConfig = {
            url: config.inlandSurfaceWaterSWPAUrl,
            layerId: '0'
        };
    }

    validate(): Observable<boolean> {
        const point = { latitude: this.casingPlanComponent.latitude, longitude: this.casingPlanComponent.longitude };
        let coordinate: Coordinate = { latitude: point.latitude, longitude: point.longitude };
        let data$ = this.getDataSource(coordinate);
        let data2$ = this.getDataSource(coordinate, this.EMSConfig);

        return data$.pipe(
            mergeMap(
                () => data2$,
                (result, result2) => {
                    if (result && result.features && result.features.length > 0 || result2 && result2.features.length > 0) {
                        this.response = result;
                        this.applicable = true;
                        this.resolved = this.casingPlan.wellSegments.findIndex(s => s.casingType.definition === CasingType.Conductor) !== -1;
                    } else {
                        this.applicable = false;
                        this.resolved = true;
                    }

                    return this.resolved;
                }));
    }
}

export class ActiveUndergroundMinesAlert extends GISAlert {
    alertType = AlertType.Error;
    appliesToCasing = [CasingType.MineString, CasingType.Conductor, CasingType.Surface];
    showOnSteps = [SegmentWizardSteps.Casing, SegmentWizardSteps.ReviewWellSegment];
    name = "Active Underground Mines";
    text = "Your proposed location lies within the limits of an active mining operation.  " +
        "Conductor should be planned assuming water will be encountered before penetrating the working of any active underground mine. (OAC 1501:9-1-08(M)(3)(a)(iii))" +
        " A mine string is required in an active underground mine please refer to ORC 1509.18, OAC 1501:9-1-08(M)(2))." +
        " Note: You may attach a supplement demonstrating that the proposed location will not penetrate the excavations of an active mine."

    setUp(config: GISConfig) {
        this.serviceConfig = {
            url: config.biWeeklyGISDataUrl,
            layerId: BiWeeklyFeatureLayers.UndergroundMines
        };
    }

    validate(): Observable<boolean> {
        const point = { latitude: this.casingPlanComponent.latitude, longitude: this.casingPlanComponent.longitude };
        let coordinate: Coordinate = { latitude: point.latitude, longitude: point.longitude };
        let data$ = this.getDataSource(coordinate);

        return data$.pipe(concatMap((result) => {
            if (result && result.features && result.features.length > 0) {
                this.response = result;
                this.applicable = true;
                this.resolved = this.casingPlan.wellSegments.findIndex(c => c.casingType.definition === CasingType.MineString) !== -1;

            } else {
                this.resolved = true;
                this.applicable = false;
            }
            return of(this.resolved);
        }));
    }
}

export class UndergroundActiveMines1000Alert extends GISAlert {
    name = "Active Underground mines 1000' Buffer";
    appliesToCasing = [CasingType.MineString, CasingType.Surface, CasingType.Conductor];
    showOnSteps = [SegmentWizardSteps.Casing, SegmentWizardSteps.ReviewWellSegment];
    text = "Your proposed location is near a mapped active underground mine, A mine string may be necessary. (ORC 1509.18, 1501:9-1-08(M)(2))";
    alertType = AlertType.Information;

    setUp(config: GISConfig) {
        this.serviceConfig = {
            url: config.biWeeklyGISDataUrl,
            layerId: BiWeeklyFeatureLayers.UndergroundMines
        };
    }

    validate(): Observable<boolean> {
        const point = { latitude: this.casingPlanComponent.latitude, longitude: this.casingPlanComponent.longitude };
        let coordinate: Coordinate = { latitude: point.latitude, longitude: point.longitude };
        let data$ = this.getDataSource(coordinate, this.serviceConfig, ServiceType.Feature, { distanceBuffer: 1000, units: "feet" });


        return data$.pipe(concatMap((result) => {
            if (result && result.features && result.features.length > 0) {
                this.response = result;
                this.applicable = true;
                this.resolved = true;

            } else {
                this.applicable = false;
                this.resolved = true;
            }
            return of(this.resolved);
        }));
    }
}

export class AbandonedUndergroundMinesAlert extends GISAlert {
    alertType = AlertType.Error;
    appliesToCasing = [CasingType.MineString, CasingType.Surface, CasingType.Intermediate1, CasingType.Intermediate2];
    showOnSteps = [SegmentWizardSteps.Casing, SegmentWizardSteps.ReviewWellSegment];
    name = "Underground Abandoned Mines (AUM)";
    text = "Your proposed location is within an abandoned underground mine area." +
        " After drilling through any underground mine void or rubble zone, casing shall be set at least thirty feet but no more than fifty feet below the base of the mine void or rubble zone and cemented at this point." +
        " A mine string shall not serve as the only water protection casing. Where a mine string isolates one or more water - bearing zones, either surface or intermediate casing shall be cemented to surface inside the mine string. (OAC 1501: 9 - 1 - 08(M)(2))."

    setUp(config: GISConfig) {
        this.serviceConfig = {
            url: config.biWeeklyGISDataUrl,
            layerId: BiWeeklyFeatureLayers.AbandonedUndergroundMines
        };
    }

    validate(): Observable<boolean> {
        const point = { latitude: this.casingPlanComponent.latitude, longitude: this.casingPlanComponent.longitude };
        let coordinate: Coordinate = { latitude: point.latitude, longitude: point.longitude };
        let data$ = this.getDataSource(coordinate);

        return data$.pipe(concatMap((result) => {
            if (result && result.features && result.features.length > 0) {
                this.response = result;
                this.applicable = true;
                const hasMine = this.casingPlan.wellSegments.findIndex(c => c.casingType.definition === CasingType.MineString) !== -1;
                const hasIntermediate = this.casingPlan.wellSegments.findIndex(c => c.casingType.definition === CasingType.Intermediate1 || c.casingType.definition === CasingType.Intermediate2) !== -1
                const hasSurface = this.casingPlan.wellSegments.findIndex(c => c.casingType.definition === CasingType.Surface) !== -1;
                this.resolved = hasMine && (hasIntermediate || hasSurface);
                return of(this.resolved);
            } else {
                this.resolved = true;
                this.applicable = false;
            }
            return of(this.resolved);
        }));
    }
}

export class UndergroundAbandonedMines1000Alert extends GISAlert {
    alertType = AlertType.Information;
    appliesToCasing = [CasingType.MineString, CasingType.Conductor, CasingType.Surface];
    showOnSteps = [SegmentWizardSteps.Casing, SegmentWizardSteps.ReviewWellSegment];
    name = "Underground Abandoned Mines 1000' Buffer";
    text = "Your proposed location is near a mapped abandoned underground mine, A mine string may be necessary. (OAC 1501:9-1-08(M)(2)";

    setUp(config: GISConfig) {
        this.serviceConfig = {
            url: config.biWeeklyGISDataUrl,
            layerId: BiWeeklyFeatureLayers.AbandonedUndergroundMines
        };
    }

    validate(): Observable<boolean> {
        const point = { latitude: this.casingPlanComponent.latitude, longitude: this.casingPlanComponent.longitude };
        let coordinate: Coordinate = { latitude: point.latitude, longitude: point.longitude };
        let data$ = this.getDataSource(coordinate, this.serviceConfig, ServiceType.Feature, { distanceBuffer: 1000, units: 'feet' });

        return data$.pipe(concatMap((result) => {
            if (result && result.features && result.features.length > 0) {
                this.response = result;
                this.applicable = true;
                this.resolved = true;

            } else {
                this.resolved = true;
                this.applicable = false;
            }
            return of(this.resolved);
        }));
    }
}

export class ClintonPinchoutAlert extends GISAlert {
    alertType = AlertType.Error;
    appliesToCasing = [CasingType.Intermediate1, CasingType.Intermediate2];
    showOnSteps = [SegmentWizardSteps.Casing, SegmentWizardSteps.ReviewWellSegment];
    name = "Clinton Pinchout";
    text = "You are drilling to a permitted hydrocarbon zone deeper than the Clinton Sandstone east of the updip pinchout," +
        " an intermediate string is required to be at 1000' deep or through the Berea, whichever is deeper. (OAC 1501:9-1-08(M)(6))";

    setUp(config: GISConfig) {
        this.serviceConfig = {
            url: config.clintonPinchoutGISLayer,
            layerId: "0"
        };
    }

    validate(): Observable<boolean> {
        const point = { latitude: this.casingPlanComponent.latitude, longitude: this.casingPlanComponent.longitude };
        let coordinate: Coordinate = { latitude: point.latitude, longitude: point.longitude };
        let data$ = this.getDataSource(coordinate);

        return data$.pipe(concatMap((result) => {
            if (result && result.features && result.features.length > 0
                && result.features[0].attributes.LOCATION === "EAST") {
                const deepestClinton = this.getDeepestClintonIndex();
                let belowClintons: boolean = false;
                for (let f = 0; f < this.casingPlanComponent.proposedFormations.length; f++) {
                    const formation = this.casingPlanComponent.proposedFormations[f];
                    const index = this.casingPlanComponent.formations.findIndex(fm => fm.code === formation);
                    belowClintons = deepestClinton < index;
                    if (belowClintons) break;
                }
                this.response = result;
                this.applicable = belowClintons;
                if (this.applicable) {
                    this.resolved = false;
                    const intermediates = this.casingPlan.wellSegments.filter(c => c.casingType.definition === CasingType.Intermediate1 || c.casingType.definition === CasingType.Intermediate2);
                    if (intermediates && intermediates.length > 0) {
                        const deepestIntermediate = Math.max.apply(Math, intermediates.map((i) => { return i.casingBottom; }));
                        this.resolved = deepestIntermediate >= 1000;
                    }
                } else {
                    this.resolved = true;
                }
            } else {
                this.applicable = false;
                this.resolved = true;
            }
            return of(this.resolved);
        }));
    }

    getDeepestClintonIndex(): number {
        const formations = this.casingPlanComponent.formations;
        const names = formations.filter(f => f.code.toUpperCase().indexOf("CLINTON") !== -1);
        let deepestClintonIndex: number = -1;
        for (let n = 0; n < names.length; n++) {
            const index = formations.findIndex(f => f.code === names[n].code);
            if (deepestClintonIndex < index) {
                deepestClintonIndex = index;
            }
        }
    return deepestClintonIndex;
    }
}

export class GasStorageFieldAlert extends GISAlert {
    alertType = AlertType.Error;
    appliesToCasing = [CasingType.Intermediate1, CasingType.Intermediate2];
    showOnSteps = [SegmentWizardSteps.Casing, SegmentWizardSteps.ReviewWellSegment];
    name = "Gas Storage Reservoir Protective Area";
    text = "Your proposed surface location is within a protected gas storage reservoir area. Intermediate casing must be set through the reservoir formation." +
        " (OAC 1501:9-1-08(M)(6))";

    setUp(config: GISConfig) {
        this.serviceConfig = {
            url: config.yearlyGISDataUrl,
            layerId: YearlyFeatureLayers.StorageFields
        };
    }

    validate(): Observable<boolean> {
        const point = { latitude: this.casingPlanComponent.latitude, longitude: this.casingPlanComponent.longitude };
        let coordinate: Coordinate = { latitude: point.latitude, longitude: point.longitude };
        let data$ = this.getDataSource(coordinate);

        return data$.pipe(concatMap((result) => {
            if (result && result.features && result.features.length > 0) {
                this.response = result;
                this.applicable = true;
                this.resolved = this.casingPlan.wellSegments.findIndex(c => c.casingType.definition === CasingType.Intermediate1 || c.casingType.definition === CasingType.Intermediate2) !== -1;
            } else {
                this.resolved = true;
                this.applicable = false;
            }
            return of(this.resolved);
        }));
    }
}

export class DriftThicknessAlert extends GISAlert {
    alertType = AlertType.Information;
    appliesToCasing = [CasingType.DrivePipe, CasingType.Conductor, CasingType.Surface];
    showOnSteps = [SegmentWizardSteps.Casing, SegmentWizardSteps.ReviewWellSegment, SegmentWizardSteps.Points];
    name = "Drift Thickness";
    text = "Your proposed location is within a drift area. Drive pipe may be driven through unconsolidated materials and need not be cemented if" +
        " there is no annular space. (OAC 1501: 9-1-08(M)(1)) Conductor casing shall be set where necessary to stabilize unconsolidated sediments."
        + " (OAC 1501:9-1-08(M)(3)(a)(i))";



    setUp(config: GISConfig) {
        this.serviceConfig = {
            url: config.glacialDriftGISUrl,
            layerId: "0"
        }
    }

    validate(): Observable<boolean> {
        const point = { latitude: this.casingPlanComponent.latitude, longitude: this.casingPlanComponent.longitude };
        let coordinate: Coordinate = { latitude: point.latitude, longitude: point.longitude };
        let data$ = this.getDataSource(coordinate, this.serviceConfig, ServiceType.Image);

        return data$.pipe(concatMap((result) => {
            if (result && result.value && result.value !== "NoData") {
                this.thickness = Number(result.value);
                this.response = result;
                this.applicable = true;
                this.resolved = true;
            } else {
                this.resolved = true;
                this.applicable = false;
            }
            return of(this.resolved);
        }));
    }
}

export class MappableUndergroundWaterAlert extends GISAlert {
    alertType = AlertType.Error;
    appliesToCasing = [CasingType.Surface, CasingType.Conductor];
    showOnSteps = [SegmentWizardSteps.Casing, SegmentWizardSteps.ReviewWellSegment];
    name = "Mappable Underground Source of Drinking Water";
    text = "Your proposed location is within a mappable USDW area. The surface casing must be at least 50'" +
        " below the depth of the base of the deepest USDW or at least 50' into competent bedrock, whichever is deeper. In this area," +
        " the [[USDWAREA]] is the USDW. (OAC 1501: 9-1-08(M)(4))";

    constructor(injector: Injector) {
        super(injector);
    }

    setUp(config: GISConfig) {
        this.serviceConfig = {
            url: config.usdwAreasUrl,
            layerId: "0"
        }
    }

    validate(): Observable<boolean> {
        const point = { latitude: this.casingPlanComponent.latitude, longitude: this.casingPlanComponent.longitude };
        let coordinate: Coordinate = { latitude: point.latitude, longitude: point.longitude };
        let data$ = this.getDataSource(coordinate);

        return data$.pipe(concatMap((result) => {
            if (result && result.features && result.features.length > 0 && result.features[0].attributes.USDWAREAS !== UNMAPPED_USDW) {
                const area = result.features[0].attributes.USDWAREAS;
                this.text = this.text.replace("[[USDWAREA]]", area);
                this.response = result;
                this.applicable = true;
                this.children.forEach(x => {
                    let child = this.casingPlanComponent.alertList.find(y => y === x);

                    if (!child)
                        this.casingPlanComponent.alertList.push(x);
                });
                this.resolved = this.casingPlan.wellSegments.findIndex(s => s.casingType.definition === CasingType.Surface) !== -1;
            } else {
                this.resolved = true;
                this.applicable = false;
            }
            return of(this.resolved);
        }));
    }
}

export class NonMappableUndergroundWaterAlert extends GISAlert {
    alertType = AlertType.Error;
    appliesToCasing = [CasingType.Surface, CasingType.Conductor];
    showOnSteps = [SegmentWizardSteps.Casing, SegmentWizardSteps.Points, SegmentWizardSteps.ReviewWellSegment];
    name = "Non-mappable Underground Source of Drinking Water";
    text = "Your proposed location is within a non-mappable USDW area. The surface casing must be at least 300' deep. (OAC 1501:9-1-08(M)(4))";
    children = [];

    constructor(injector: Injector) {
        super(injector);
        this.children = [
            new PerennialStreamsNonMappableUSDWAlert(injector),
            new NonMappableUSDWAlert(injector),
            new WaterWellsNonMappableUSDWAlert(injector)
        ];
    }


    setUp(config: GISConfig) {
        this.serviceConfig = {
            url: config.usdwAreasUrl,
            layerId: "0"
        }
    }

    validate(): Observable<boolean> {
        const point = { latitude: this.casingPlanComponent.latitude, longitude: this.casingPlanComponent.longitude };
        let coordinate: Coordinate = { latitude: point.latitude, longitude: point.longitude };
        let data$ = this.getDataSource(coordinate);

        return data$.pipe(concatMap((result) => {
            if (result && result.features && result.features.length > 0 && result.features[0].attributes.USDWAREAS === UNMAPPED_USDW) {
                this.response = result;
                this.applicable = true;
                this.children.forEach(c => {
                    let missing = this.casingPlanComponent.constructionService.observableAlerts.findIndex(x => x === c) === -1;
                    if (missing) {
                        this.casingPlanComponent.constructionService.observableAlerts.push(c);
                    }
                });

                let casing = this.casingPlan.wellSegments.find(x => x.casingType.definition === CasingType.Surface);
                this.resolved = casing && casing.casingBottom >= 300;
            } else {
                this.applicable = false;
                this.resolved = true;
            }
            return of(this.resolved);
        }));
    }
}

export class WaterWellsNonMappableUSDWAlert extends GISAlert {
    alertType = AlertType.Error;
    appliesToCasing = [CasingType.Surface, CasingType.Conductor];
    showOnSteps = [SegmentWizardSteps.Casing, SegmentWizardSteps.ReviewWellSegment];
    name = "Water Wells in non-mappable USDW";
    text = "Your proposed location is within 500' of a water well. In non-mappable USDW areas the surface casing must be at least 50'" +
        " below the base of the lowest spring or deepest water well within a 500 ft radius of the proposed oil and gas well. (OAC 1501: 9-1-08(M)(4)(f)).";

    setUp(config: GISConfig) {
        this.serviceConfig = {
            url: config.dailyGISDataUrl,
            layerId: DailyFeatureLayers.WaterWells
        }
    }

    validate(): Observable<boolean> {
        const point = { latitude: this.casingPlanComponent.latitude, longitude: this.casingPlanComponent.longitude };
        let coordinate: Coordinate = { latitude: point.latitude, longitude: point.longitude };

        let data$ = this.getDataSource(coordinate, this.serviceConfig, ServiceType.Feature, { distanceBuffer: 500, units: 'feet' });

        return data$.pipe(concatMap((result) => {
            if (result && result.features && result.features.length > 0) {
                this.response = result;
                this.applicable = true;
                this.resolved = this.casingPlan.wellSegments.findIndex(s => s.casingType.definition === CasingType.Surface) !== -1;
            } else {
                this.applicable = false;
            }
            this.isApplicable$.next(this.applicable);
            return of(this.resolved);
        }));
    }
}

export class PerennialStreamsNonMappableUSDWAlert extends GISAlert {
    alertType = AlertType.Error;
    appliesToCasing = [CasingType.Surface, CasingType.Conductor];
    showOnSteps = [SegmentWizardSteps.Casing, SegmentWizardSteps.ReviewWellSegment];
    name = "Perennial Streams in non-mappable USDW";
    text = "In non-mapable USDW area the surface casing must be at least 100' below the perennial stream elevation.  (OAC 1501:9-1-08(M)(4)(f)).";

    NHDAreaConfig: GISServiceConfig;
    NHDWaterBodyConfig: GISServiceConfig;

    setUp(config: GISConfig) {
        this.serviceConfig = {
            url: config.yearlyGISDataUrl,
            layerId: YearlyFeatureLayers.NHDFlowline,
        }
    }

    validate(): Observable<boolean> {
        const point = { latitude: this.casingPlanComponent.latitude, longitude: this.casingPlanComponent.longitude };
        let coordinate: Coordinate = { latitude: point.latitude, longitude: point.longitude };
        this.resolved = false;
        let data$ = this.getDataSource(coordinate, this.serviceConfig, ServiceType.Feature, { distanceBuffer: 500, units: 'feet' });

        return data$.pipe(concatMap((result) => {
            if (result && result.features && result.features.length > 0) {
                this.response = result;
                this.applicable = true;
            } else {
                this.applicable = false;
            }
            this.resolved = true;
            return of(this.resolved);
        }));
    }
}


export class UndergroundInjectionControlAORAlert extends GISAlert {
    alertType = AlertType.Information;
    appliesToCasing = [CasingType.Intermediate1, CasingType.Intermediate2, CasingType.Production, CasingType.OpenHole, CasingType.Liner1, CasingType.Tubing];
    showOnSteps = [SegmentWizardSteps.Casing, SegmentWizardSteps.ReviewWellSegment];
    name = "Underground Injection Control Area of Review (AOR)";
    text = "Your proposed location is within the area of review of an injection zone or solution mining operation."
        + " Pending additional review, you may be required to amend your casing plan. (ORC 1509.06(F))";

    setUp(config: GISConfig) {
        this.serviceConfig = {
            url: config.dailyGISDataUrl,
            layerId: DailyFeatureLayers.InjectionWellAORQtr
        };
    }

    validate(): Observable<boolean> {
        const point = { latitude: this.casingPlanComponent.latitude, longitude: this.casingPlanComponent.longitude };
        let coordinate: Coordinate = { latitude: point.latitude, longitude: point.longitude };
        let data$ = this.getDataSource(coordinate);

        return data$.pipe(concatMap((result) => {
            if (result && result.features && result.features.length > 0) {
                this.response = result;
                this.applicable = true;
            } else {
                this.applicable = false;
            }
            this.resolved = true;
            return of(this.resolved);
        }));
    }
}

export class HydrogenSulfideAlert extends GISAlert {
    alertType = AlertType.Information;
    appliesToCasing = [CasingType.Intermediate1, CasingType.Intermediate2, CasingType.Production, CasingType.OpenHole, CasingType.Liner1, CasingType.Tubing];
    showOnSteps = [SegmentWizardSteps.Casing, SegmentWizardSteps.ReviewWellSegment];
    name = "Hydrogen Sulfide";
    text = "Your proposed surface location is within a H2S township, you shall use sulfate resistant cement to protect casing strings that penetrate hydrogen"
        + " sulfide-bearing zones, if H2S encountered. (OAC 1501: 9-1-08(J)(7)(ORC 1509.06(F))";

    setUp(config: GISConfig) {
        this.serviceConfig = {
            url: config.yearlyGISDataUrl,
            layerId: YearlyFeatureLayers.H2S
        };
    }

    validate(): Observable<boolean> {
        const point = { latitude: this.casingPlanComponent.latitude, longitude: this.casingPlanComponent.longitude };
        let coordinate: Coordinate = { latitude: point.latitude, longitude: point.longitude };
        let data$ = this.getDataSource(coordinate);

        return data$.pipe(concatMap((result) => {
            if (result && result.features && result.features.length > 0) {
                this.response = result;
                this.applicable = result.features[0].attributes["H2S"] !== null;
            }
            this.resolved = true;
            return of(this.resolved);
        }));
    }
}

export class ThinTillAlert extends GISAlert {
    alertType = AlertType.Information;
    appliesToCasing = [CasingType.Surface, CasingType.Conductor];
    showOnSteps = [SegmentWizardSteps.Casing, SegmentWizardSteps.ReviewWellSegment];
    name = "Thin Till";
    text = "Your proposed location is within an area of fractured bedrock with thin till. Shallow aquifers must be isolated. (ORC 1509.06(F))";

    setUp(config: GISConfig) {
        this.serviceConfig = {
            url: config.yearlyGISDataUrl,
            layerId: YearlyFeatureLayers.ThinTill
        };
    }

    validate(): Observable<boolean> {
        const point = { latitude: this.casingPlanComponent.latitude, longitude: this.casingPlanComponent.longitude };
        let coordinate: Coordinate = { latitude: point.latitude, longitude: point.longitude };
        let data$ = this.getDataSource(coordinate);

        return data$.pipe(concatMap((result) => {
            if (result && result.features && result.features.length > 0) {
                this.applicable = true;
                this.response = result;
                this.resolved = true;
            }
            return of(this.resolved);
        }));
    }
}

export class LakePlainAlert extends GISAlert {
    alertType = AlertType.Information;
    appliesToCasing = [CasingType.Surface, CasingType.Intermediate1, CasingType.Intermediate2];
    showOnSteps = [SegmentWizardSteps.Casing, SegmentWizardSteps.ReviewWellSegment];
    name = "Lake Plain";
    text = "Your proposed location lies within the Lake Plain region. Shallow sand and gravel aquifers are vulnerable to contamination and" +
        " require additional precautions. (ORC 1509.06(F))";

    setUp(config: GISConfig) {
        this.serviceConfig = {
            url: config.yearlyGISDataUrl,
            layerId: YearlyFeatureLayers.MeccaPoolLakePlain
        };
    }

    validate(): Observable<boolean> {
        const point = { latitude: this.casingPlanComponent.latitude, longitude: this.casingPlanComponent.longitude };
        let coordinate: Coordinate = { latitude: point.latitude, longitude: point.longitude };
        let data$ = this.getDataSource(coordinate);

        return data$.pipe(concatMap((result) => {
            if (result && result.features && result.features.length > 0) {
                this.response = result;
                const plain = result.features.find(f => f.attributes["Name"].toString() === "Lake Plain Region")
                this.applicable = isDefined(plain);
            }
            this.resolved = true;
            return of(this.resolved);
        }));
    }
}

export class MeccaPoolAlert extends GISAlert {
    alertType = AlertType.Information;
    appliesToCasing = [CasingType.Surface, CasingType.Conductor];
    showOnSteps = [SegmentWizardSteps.Casing, SegmentWizardSteps.ReviewWellSegment];
    name = "Mecca Pool";
    text = "Your proposed surface location is located in the Mecca Pool in Trumbull County. In this area, Lower Mississippian Sandstones" +
        " may provide domestic water supplies. The Berea Sandstone is both an aquifer and an oil reservoir. Below the Berea, the Cussewago Sandstone is an" +
        " aquifer, which is hydraulically connected to Mosquito Creek Lake. There also may be shallow water wells or springs in the area. In order to protect" +
        " the fresh water multiple casing strings may be necessary. (ORC 1509.06(F))";

    setUp(config: GISConfig) {
        this.serviceConfig = {
            url: config.yearlyGISDataUrl,
            layerId: YearlyFeatureLayers.MeccaPoolLakePlain
        };
    }

    validate(): Observable<boolean> {
        const point = { latitude: this.casingPlanComponent.latitude, longitude: this.casingPlanComponent.longitude };
        let coordinate: Coordinate = { latitude: point.latitude, longitude: point.longitude };
        let data$ = this.getDataSource(coordinate);

        return data$.pipe(concatMap((result) => {
            if (result && result.features && result.features.length > 0) {
                this.response = result;
                const mecca = result.features.find(f => f.attributes["Name"].toString() === "Mecca Pool");
                this.applicable = isDefined(mecca);
            }
            this.resolved = true;
            return of(this.resolved);
        }));
    }
}

export class TrempealeauAlert extends GISAlert {
    alertType = AlertType.Information;
    appliesToCasing = [CasingType.Surface];
    name = "Trempealeau";
    showOnSteps = [SegmentWizardSteps.Casing]
    text = "Your proposed surface location is in Morrow County through the Trempealeau formation. Underground sources of drinking water require protection. " +
        "(ORC 1509.06(F))";

    setUp(config: GISConfig) {
        this.serviceConfig = {
            url: config.yearlyGISDataUrl,
            layerId: YearlyFeatureLayers.Trempealeau
        }
    }

    validate(): Observable<boolean> {
        const point = { latitude: this.casingPlanComponent.latitude, longitude: this.casingPlanComponent.longitude };
        let coordinate: Coordinate = { latitude: point.latitude, longitude: point.longitude };
        let data$ = this.getDataSource(coordinate);

        return data$.pipe(concatMap((result) => {
            if (result && result.features && result.features.length > 0) {
                this.response = result;
                this.applicable = true;
            }
            this.resolved = true;
            return of(this.resolved);
        }));
    }
}

export class KarstAreaAlert extends GISAlert {
    name = "Karst Area";
    text = "This well location is within the boundaries of a \"Probable karst area\" please take all due precautions. ";
    appliesToCasing = [CasingType.Surface, CasingType.Conductor];
    showOnSteps = [SegmentWizardSteps.Casing, SegmentWizardSteps.ReviewWellSegment];
    alertType = AlertType.Information;

    setUp(config: GISConfig) {
        this.serviceConfig = {
            url: config.yearlyGISDataUrl,
            layerId: YearlyFeatureLayers.Karst
        }
    }

    validate(): Observable<boolean> {
        const point = { latitude: this.casingPlanComponent.latitude, longitude: this.casingPlanComponent.longitude };
        let coordinate: Coordinate = { latitude: point.latitude, longitude: point.longitude };
        let data$ = this.getDataSource(coordinate);

        return data$.pipe(concatMap((result) => {
            if (result && result.features && result.features.length > 0) {
                this.response = result;
                this.applicable = true;

            }
            this.resolved = true;
            return of(this.resolved);
        }))
    }
}

export class SolutionMiningAreaAlert extends GISAlert {
    alertType = AlertType.Information;
    appliesToCasing = [CasingType.Intermediate1, CasingType.Intermediate2, CasingType.Production, CasingType.OpenHole, CasingType.Liner1, CasingType.Tubing];
    showOnSteps = [SegmentWizardSteps.Casing, SegmentWizardSteps.ReviewWellSegment];
    name = "Solution Mining Area";
    text = "Your proposed location is within a solution mining area. There is a possibility this well may encounter pressurized fluids and/or gas" +
        " with unusually high flow rates from the Bass Island Limestone and Salina group.";

    setUp(config: GISConfig) {
        this.serviceConfig = {
            url: config.yearlyGISDataUrl,
            layerId: YearlyFeatureLayers.SolutionsMining
        };
    }

    validate(): Observable<boolean> {
        const point = { latitude: this.casingPlanComponent.latitude, longitude: this.casingPlanComponent.longitude };
        let coordinate: Coordinate = { latitude: point.latitude, longitude: point.longitude };
        let data$ = this.getDataSource(coordinate);

        return data$.pipe(concatMap((result) => {
            if (result && result.features && result.features.length > 0) {
                this.response = result;
                this.applicable = true;
            }
            this.resolved = true;
            return of(this.resolved);
        }));
    }
}

export class NewWellsAlert extends Alert {
    alertType = AlertType.Error;
    appliesToCasing = [CasingType.Surface, CasingType.MineString];
    name = "All new wells";
    showOnSteps = [SegmentWizardSteps.Casing, SegmentWizardSteps.ReviewWellSegment];
    text = "Based on Ohio Code surface casing is required to be set a minimum of 50' below the deepest USDW and shall be cemented before drilling through hydrocarbon-bearing flow zones," +
        " or flow zones containing total dissolved solids exceeding 10,000 mg/L. ORC 1509.17, OAC 1501:9-1-08(M)(4)";

    validate(): boolean {
        this.applicable = this.casingPlanComponent.isNewWell;

        if (this.applicable) {
            const surface = this.casingPlanComponent.casingPlan.wellSegments.findIndex(s => s.casingType.definition === CasingType.Surface);
            this.resolved = surface !== -1;
        }

        return this.resolved;
    }
}

export class DefinedHorizontalWellAlert extends Alert {
    alertType = AlertType.Error;
    appliesToCasing = [CasingType.Intermediate1, CasingType.Intermediate2];
    showOnSteps = [SegmentWizardSteps.Casing, SegmentWizardSteps.Points, SegmentWizardSteps.ReviewWellSegment];
    name = "Defined Horizontal Well";
    text = "An intermediate casing string is required set through the Berea or 1000', whichever is deeper, when drilling a \"Horizontal Well\"" +
        " (ORC 1509.01(GG))";

    validate(): boolean {
        this.applicable = false;
        let formations = this.casingPlanComponent.proposedFormations;
        const requiredFormations = ["UTICASHALE", "MARCELLUSSHALE", "POINTPLEASANTFORMATION"];
        if (this.casingPlan.wellDirection === WellDirection.Horizontal || this.casingPlan.wellDirection === WellDirection.OtherHorizontal) {
            for (let r = 0; r < requiredFormations.length; r++) {
                const index = formations.findIndex(f => f === requiredFormations[r]);
                this.applicable = index !== -1;
                if (this.applicable) break;
            }
        }
        if (this.applicable) {
            const intermediate = this.casingPlan.wellSegments.find(s => s.casingType.definition === CasingType.Intermediate1 || s.casingType.definition === CasingType.Intermediate2);
            this.resolved = (isDefined(intermediate) && intermediate.casingBottom >= 1000);
        }
        return this.resolved;
    }
}

export class EORAlert extends Alert {
    alertType = AlertType.Error;
    appliesToCasing = [CasingType.Tubing];
    name = "SWIW, EOR";
    showOnSteps = [SegmentWizardSteps.Casing, SegmentWizardSteps.Points, SegmentWizardSteps.ReviewWellSegment];
    text = "Packer and tubing is required. (ORC, (OAC 1501:9-3-05(A)(3)), (OAC 1501:9-5-09 (A)(3))";

    constructor(injector: Injector) {
        super(injector);
        this.applicable = false;
    }

    validate(): boolean {
        const reqList = [{ description: "EORP", type: "ER_R" }, { description: "SW_R", type: "" }] as WellType[];
        this.applicable = reqList.findIndex(t => t.description === this.casingPlanComponent.wellTypeDescription && t.type === this.casingPlanComponent.wellType) !== -1;

        if (this.applicable) {
            let tubing = this.casingPlanComponent.casingPlan.wellSegments.find(s => s.casingType.definition === CasingType.Tubing);
            if (isDefined(tubing)) {
                this.resolved = isDefined(tubing.packerDepth) && tubing.packerDepth > 0;
            } else {
                this.resolved = false;
            }
        }

        return this.resolved;
    }
}

export class IntermediateStringAlert extends Alert {
    alertType = AlertType.Information;
    appliesToCasing = [CasingType.Intermediate1, CasingType.Intermediate2];
    name = "Intermediate String";
    showOnSteps = [SegmentWizardSteps.Casing, SegmentWizardSteps.ReviewWellSegment];
    text = "Intermediate string may meet the requirements of a production string if all flow zones, lost circulation zones," +
        " and geologic hazards are isolated above the producable formation.  OAC 1501:9-1-08(M)(6)";

    constructor(injector: Injector) {
        super(injector);
        this.applicable = false;
    }

    validate(): boolean {
        this.applicable = this.casingPlanComponent.casingPlan.wellSegments.findIndex(s => s.casingType.definition === CasingType.Intermediate1 || s.casingType.definition === CasingType.Intermediate2) !== -1;
        this.resolved = true;
        return this.applicable;
    }
}

export class ProductionStringAlert extends Alert {
    appliesToCasing = [CasingType.Production];
    alertType = AlertType.Information;

    name = "Production String";
    showOnSteps = [SegmentWizardSteps.Casing, SegmentWizardSteps.ReviewWellSegment];
    text = " All flow zones including strata containing flow zones where total dissolved solids exceed 10,000 mg/L, hydrocarbons, and hydrogen" +
        " sulfide - bearing flow zones must be isolated from the producing formation.OAC 1501:9-1-08(M)(7)";

    constructor(injector: Injector) {
        super(injector);
        this.applicable = false;
    }

    validate(): boolean {
        this.applicable = this.casingPlanComponent.casingPlan.wellSegments.findIndex(s => s.casingType.definition === CasingType.Production) !== -1;
        return this.applicable;
    }
}

export class OpenHoleAlert extends Alert {
    alertType = AlertType.Information;
    appliesToCasing = [CasingType.OpenHole];
    name = "Open Hole";
    showOnSteps = [SegmentWizardSteps.Casing, SegmentWizardSteps.ReviewWellSegment];
    text = "Either intermediate or production casing has been set to isolate any flow zones, lost circulation zones," +
        " and geologic hazards above the producable formation. OAC 1501:9-1-08(M)(6)";

    constructor(injector: Injector) {
        super(injector);
        this.applicable = false;
    }

    validate(): boolean {
        this.applicable = this.casingPlanComponent.casingPlan.wellSegments.findIndex(s => s.casingType.definition === CasingType.OpenHole) !== -1;
        this.resolved;
        return this.applicable;
    }
}

export class DrivePipeLieuConductorAlert extends Alert {
    alertType = AlertType.Information;
    appliesToCasing = [CasingType.DrivePipe]
    showOnSteps = [SegmentWizardSteps.Casing, SegmentWizardSteps.ReviewWellSegment];
    name = "Drive Pipe in lieu of conductor";
    text = "Although drive pipe may meet conductor requirements user must still account for conductor casing in plan. A waiver may be obtained if" +
        " inspector witnesses and confirms that the drive pipe meets conductor requirements.";

    validate(): boolean {
        const alerts = this.casingPlanComponent.alertList.filter(a => this.alertsRequiringConductor().findIndex(name => a.name === name) !== -1);
        this.applicable = alerts.findIndex(a => a.applicable) !== -1;
        this.resolved = true;
        return this.resolved;
    }

    alertsRequiringConductor(): string[] {
        return ["Non-mappable USDW, no water well or spring", "Shallow Surface Casing", "Municipal Wellhead Protection Area", "Active Underground Mines", "Drift Thickness"];
    }
}

export class UICAlert extends Alert {
    alertType = AlertType.Information;
    appliesToCasing = [CasingType.Intermediate1, CasingType.Intermediate2, CasingType.Production, CasingType.Liner1];
    showOnSteps = [SegmentWizardSteps.Casing, SegmentWizardSteps.ReviewWellSegment];
    name = "UIC";
    text = "In order properly isolate injected fluids, casing shall be mechanically centralized and enclosed in cement to a height no less than 300" +
        " feet above the top of the injection zone, casing shall be set set to a minimum depth greater than or equal to the top of the injection zone." +
        " 1501:9-3-05(A)(2)";

    validate(): boolean {        
        const reqList = [{ description: "EORP", type: "ER_R" }, { description: "", type: "SW_R" }, { description: "", type: "SM_R" }] as WellType[];
        this.applicable = reqList.findIndex(t => t.type === this.casingPlanComponent.wellType && t.description === this.casingPlanComponent.wellTypeDescription) !== -1;
        this.resolved = true;
        return this.resolved;
    }
}

export class AirRotaryToolInUrbanAreaAlert extends Alert {
    alertType = AlertType.Information;
    appliesToCasing = [CasingType.Concentric, CasingType.Conductor, CasingType.Intermediate1, CasingType.Intermediate1Entry, CasingType.Intermediate2,
                        CasingType.Intermediate2Entry, CasingType.MineString, CasingType.OpenHole, CasingType.Production, CasingType.Surface];
    name = "Air Rotary Tool In an Urban Area";
    showOnSteps = [SegmentWizardSteps.Casing, SegmentWizardSteps.Tools, SegmentWizardSteps.ReviewWellSegment];
    text = "Pursuant to ORC 1509.073, fluid drilling conditions must be established prior to penetrating the Onondaga Limestone.";

    constructor(injector: Injector) {
        super(injector);
        this.applicable = false;
    }

    validate(): boolean {
        this.applicable = this.casingPlanComponent.casingPlan.isUrbanizedArea && this.casingPlanComponent.casingPlan.wellSegments.findIndex(s => s.drillingEquipment.code === "RTA") !== -1;;
        this.resolved = true;
        return this.applicable;
    }
}
